import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Activity, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const WATCH_KPIS = [
  { key: "new_leads", label: "New Leads" },
  { key: "qualified_leads", label: "Qualified Leads" },
  { key: "appointments_booked", label: "Appts Booked" },
  { key: "appointments_completed", label: "Appts Completed" },
  { key: "contracts_signed", label: "Contracts Signed" },
  { key: "new_plan_signups", label: "Plan Signups" },
  { key: "plan_mrr_usd", label: "Plan MRR", money: true },
  { key: "plan_attach_rate_pct", label: "Attach Rate %" },
];

function Sparkline({ values, height = 36 }) {
  if (!values || values.length === 0) {
    return <div className="h-9 flex items-center text-[11px] text-zinc-600">no data</div>;
  }
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 120;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={height} className="text-amber-300" aria-hidden>
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={points} />
    </svg>
  );
}

export default function AnalyticsView() {
  const [snapshots, setSnapshots] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);

  const reload = async () => {
    try {
      const [snaps, anoms] = await Promise.all([
        crmApi.analyticsSnapshots(30),
        crmApi.analyticsAnomalies({ acknowledged: false, limit: 50 }),
      ]);
      setSnapshots(snaps || []);
      setAnomalies(anoms || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load analytics");
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await crmApi.analyticsUpsertToday();
        const [snaps, anoms] = await Promise.all([
          crmApi.analyticsSnapshots(30),
          crmApi.analyticsAnomalies({ acknowledged: false, limit: 50 }),
        ]);
        if (active) {
          setSnapshots(snaps || []);
          setAnomalies(anoms || []);
        }
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load analytics");
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  const detect = async () => {
    setDetecting(true);
    try {
      const res = await crmApi.analyticsDetect();
      toast.success(`Anomaly scan complete — ${res.detected} new`);
      reload();
    } catch (exc) {
      toast.error(exc.message || "Scan failed");
    } finally { setDetecting(false); }
  };

  const ack = async (id) => {
    try { await crmApi.analyticsAck(id); toast.success("Acknowledged"); reload(); }
    catch (exc) { toast.error(exc.message || "Failed"); }
  };

  if (loading) {
    return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…</div>;
  }

  const today = snapshots[snapshots.length - 1] || {};
  const valuesByKpi = (key) => snapshots.map((s) => Number(s?.[key] ?? 0));

  return (
    <div className="space-y-6" data-testid="owner-analytics">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-zinc-400 mt-1">Riley&rsquo;s workspace. Daily KPI snapshots + 30-day trend lines + anomaly detection. KPIs scale with Phase B intake.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reload} className="text-zinc-300"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={detect} disabled={detecting} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="detect-anomalies-btn">
            {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Activity className="h-4 w-4 mr-1" /> Detect anomalies</>}
          </Button>
        </div>
      </div>

      {anomalies.length > 0 && (
        <Card className="bg-rose-500/5 border-rose-500/30" data-testid="anomaly-panel">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-rose-200 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Open anomalies ({anomalies.length})</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-rose-500/20">
              {anomalies.map((a) => (
                <li key={a.id} className="py-2 flex items-center justify-between gap-3" data-testid={`anomaly-${a.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-white">{a.kpi}</span>
                      <Badge variant="outline" className={a.severity === "critical" ? "border-rose-500/40 text-rose-200" : "border-amber-500/40 text-amber-200"}>{a.severity}</Badge>
                      <span className="text-[11px] text-zinc-500">{new Date(a.detected_at).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-zinc-300 mt-1">{a.message}</div>
                  </div>
                  <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300" onClick={() => ack(a.id)} data-testid={`ack-${a.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Ack
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-white">30-day KPI trends</CardTitle></CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <div className="text-sm text-zinc-500">No snapshots yet. Phase B lead-flow will populate these once active.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {WATCH_KPIS.map(({ key, label, money }) => (
                <div key={key} className="rounded-lg border border-zinc-800 bg-black/60 p-3" data-testid={`kpi-${key}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
                  <div className="text-xl font-semibold text-white mt-1">
                    {money ? `$${Number(today?.[key] || 0).toLocaleString()}` : Number(today?.[key] || 0).toLocaleString()}
                  </div>
                  <div className="mt-2"><Sparkline values={valuesByKpi(key)} /></div>
                  <div className="text-[10px] text-zinc-500 mt-1">{snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
